import React, { useState, useEffect } from 'react';
import { Save, Edit, X, User, Settings, Printer, Package, Truck, Database, Wifi, Shield, Bell, RefreshCw } from 'lucide-react';
import { Card } from '../../common/Card';
import { Button } from '../../common/Button';
import { userProfileService } from '../../../services/userProfileService';
import { printingService } from '../../../services/printingService';
import { UserProfileDto, UpdateUserProfileRequest } from '../../../types/userProfile';
import { Printer as PrintNodePrinter } from '../../../types/printNode';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/I18nProvider';
import { SupportedLanguage } from '../../../i18n';

interface ProfileSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isEditing?: boolean;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({ title, icon, children, isEditing = false }) => (
  <Card className="p-6">
    <div className="flex items-center gap-2 mb-4">
      {icon}
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      {isEditing && <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">Redigerer</span>}
    </div>
    <div className="space-y-4">
      {children}
    </div>
  </Card>
);

interface ProfileFieldProps {
  label: string;
  value: string | number | boolean | Date | undefined | null;
  type?: 'text' | 'number' | 'boolean' | 'date' | 'select';
  isEditing?: boolean;
  onChange?: (value: string | number | boolean) => void;
  readOnly?: boolean;
  options?: { value: string; label: string }[];
}

const ProfileField: React.FC<ProfileFieldProps> = ({ 
  label, 
  value, 
  type = 'text', 
  isEditing = false, 
  onChange,
  readOnly = false,
  options = []
}) => {
  const formatValue = (val: any) => {
    if (val === null || val === undefined) return 'Ikke angitt';
    if (type === 'boolean') return val ? 'Aktivert' : 'Deaktivert';
    if (type === 'date' && val) return new Date(val).toLocaleDateString('no-NO');
    return val.toString();
  };

  if (isEditing && !readOnly) {
    if (type === 'boolean') {
      return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="font-medium text-gray-700">{label}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{value ? 'Aktivert' : 'Deaktivert'}</span>
            <button
              onClick={() => onChange?.(!value)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                value ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                value ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      );
    }

    if (type === 'select') {
      return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="font-medium text-gray-700 w-1/3">{label}</span>
          <select
            value={value?.toString() || ''}
            onChange={(e) => onChange?.(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <span className="font-medium text-gray-700 w-1/3">{label}</span>
        <input
          type={type}
          value={value?.toString() || ''}
          onChange={(e) => {
            const newValue = type === 'number' ? Number(e.target.value) : e.target.value;
            onChange?.(newValue);
          }}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <span className="font-medium text-gray-700">{label}</span>
      <span className={`text-gray-600 ${
        type === 'boolean' ? (value ? 'text-green-600' : 'text-red-600') : ''
      }`}>
        {formatValue(value)}
      </span>
    </div>
  );
};

interface PrinterSelectFieldProps {
  label: string;
  value: number | undefined | null;
  printers: PrintNodePrinter[];
  isEditing?: boolean;
  onChange?: (value: number | null) => void;
  loading?: boolean;
}

const PrinterSelectField: React.FC<PrinterSelectFieldProps> = ({
  label,
  value,
  printers,
  isEditing = false,
  onChange,
  loading = false
}) => {
  // Find printer name from ID for display
  const getPrinterName = (printerId: number | undefined | null): string => {
    if (!printerId) return 'Ikke angitt';
    const printer = printers.find(p => p.id === printerId);
    return printer?.name || `Ukjent skriver (ID: ${printerId})`;
  };

  if (isEditing) {
    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <span className="font-medium text-gray-700 w-1/3">{label}</span>
        <select
          value={value ?? ''}
          onChange={(e) => {
            const newValue = e.target.value ? parseInt(e.target.value, 10) : null;
            onChange?.(newValue);
          }}
          disabled={loading}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">-- Velg skriver --</option>
          {printers.map(printer => (
            <option key={printer.id} value={printer.id}>
              {printer.name} {printer.default ? '(Standard)' : ''}
            </option>
          ))}
        </select>
        {loading && (
          <RefreshCw size={16} className="ml-2 animate-spin text-blue-500" />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <span className="font-medium text-gray-700">{label}</span>
      <span className="text-gray-600">{getPrinterName(value)}</span>
    </div>
  );
};

export const UserProfile: React.FC = () => {
  const { user } = useAuth();
  const { language, setLanguage } = useTranslation();
  const [profile, setProfile] = useState<UserProfileDto | null>(null);
  const [editingProfile, setEditingProfile] = useState<UserProfileDto | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PrintNode printers state
  const [printers, setPrinters] = useState<PrintNodePrinter[]>([]);
  const [printersLoading, setPrintersLoading] = useState(false);
  const [printersError, setPrintersError] = useState<string | null>(null);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userProfileService.getCurrentUserProfile();
      setProfile(data);
      setEditingProfile(data);
    } catch (err) {
      setError('Kunne ikke laste profil. Vennligst prøv igjen.');
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPrinters = async () => {
    if (!user?.companyNo) return;

    try {
      setPrintersLoading(true);
      setPrintersError(null);
      const availablePrinters = await printingService.getAvailablePrinters(user.companyNo);
      setPrinters(availablePrinters);
    } catch (err) {
      setPrintersError('Kunne ikke laste skrivere fra PrintNode.');
      console.error('Failed to load printers:', err);
    } finally {
      setPrintersLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    loadPrinters();
  }, [user?.companyNo]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditingProfile(profile);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingProfile(profile);
  };

  const handleSave = async () => {
    if (!editingProfile) return;

    try {
      setSaving(true);
      setError(null);

      // Create update request from editing profile
      const updateRequest: UpdateUserProfileRequest = {
        companyNo: editingProfile.companyNo,
        profileID: editingProfile.profileID,
        profileName: editingProfile.profileName,
        httpHostname: editingProfile.httpHostname,
        httpPort: editingProfile.httpPort,
        pwd: editingProfile.pwd,
        langID: editingProfile.langID,
        protocol: editingProfile.protocol,
        tcpHostname: editingProfile.tcpHostname,
        tcpPort: editingProfile.tcpPort,
        operator: editingProfile.operator,
        stockCountingActive: editingProfile.stockCountingActive,
        goodsReceptionActive: editingProfile.goodsReceptionActive,
        pickListActive: editingProfile.pickListActive,
        pickListVersion: editingProfile.pickListVersion,
        pickListDetailView: editingProfile.pickListDetailView,
        operatorPwdRequired: editingProfile.operatorPwdRequired,
        operatorRequired: editingProfile.operatorRequired,
        connectOnStartup: editingProfile.connectOnStartup,
        pListApplyChangesEvent: editingProfile.pListApplyChangesEvent,
        pListLastSaveServer: editingProfile.pListLastSaveServer,
        pListLastSaveClient: editingProfile.pListLastSaveClient,
        orderLineSortOrder: editingProfile.orderLineSortOrder,
        userdefinedExtraInfoField: editingProfile.userdefinedExtraInfoField,
        userdefinedExtraInfoLabel: editingProfile.userdefinedExtraInfoLabel,
        showItemPicureYesNo: editingProfile.showItemPicureYesNo,
        lastPictureSync: editingProfile.lastPictureSync,
        closePListOnDeviceYesNo: editingProfile.closePListOnDeviceYesNo,
        barCodeConfirmItem: editingProfile.barCodeConfirmItem,
        userdefinedExtraInfoField2: editingProfile.userdefinedExtraInfoField2,
        labelExportFolder: editingProfile.labelExportFolder,
        packageSlipPrinterName: editingProfile.packageSlipPrinterName,
        printShippingLabelsYesNo: editingProfile.printShippingLabelsYesNo,
        initShippingLabelsYesNo: editingProfile.initShippingLabelsYesNo,
        packageSlipERPYesNo: editingProfile.packageSlipERPYesNo,
        shippingLabelsERPYesNo: editingProfile.shippingLabelsERPYesNo,
        consignmentNoteERPYesNo: editingProfile.consignmentNoteERPYesNo,
        shippingLabelsPrinterName: editingProfile.shippingLabelsPrinterName,
        consignmentNotePrinterName: editingProfile.consignmentNotePrinterName,
        grLastSaveServer: editingProfile.grLastSaveServer,
        grLastSaveClient: editingProfile.grLastSaveClient,
        closeGROnDeviceYesNo: editingProfile.closeGROnDeviceYesNo,
        defaultWarehouse: editingProfile.defaultWarehouse,
        printerID: editingProfile.printerID,
        qtyReservedAsQtyPicked: editingProfile.qtyReservedAsQtyPicked,
        scLastSaveServer: editingProfile.scLastSaveServer,
        scLastSaveClient: editingProfile.scLastSaveClient,
        closeSCOnDeviceYesNo: editingProfile.closeSCOnDeviceYesNo,
        operatorTxtVisible: editingProfile.operatorTxtVisible,
        operatorTxtLabel: editingProfile.operatorTxtLabel,
        stLastSaveServer: editingProfile.stLastSaveServer,
        stLastSaveClient: editingProfile.stLastSaveClient,
        scrLastSaveServer: editingProfile.scrLastSaveServer,
        scrLastSaveClient: editingProfile.scrLastSaveClient,
        barCodeConfirmPackage: editingProfile.barCodeConfirmPackage,
        packageLabelExportFolder: editingProfile.packageLabelExportFolder,
        packagePrinterID: editingProfile.packagePrinterID,
        packageLabelAutoPrint: editingProfile.packageLabelAutoPrint,
        packageCheckOutYesNo: editingProfile.packageCheckOutYesNo,
        packingSlipCheckOutYesNo: editingProfile.packingSlipCheckOutYesNo,
        grUseSTAND005: editingProfile.grUseSTAND005,
        plUseSTAND005: editingProfile.plUseSTAND005,
        plstand005Type: editingProfile.plstand005Type,
        pListConfirmationYesNo: editingProfile.pListConfirmationYesNo,
        useLocationSensor: editingProfile.useLocationSensor,
        stcTransCorrectionDefault: editingProfile.stcTransCorrectionDefault,
        closeSTOnDeviceYesNo: editingProfile.closeSTOnDeviceYesNo,
        closeSCROnDeviceYesNo: editingProfile.closeSCROnDeviceYesNo,
        userdefinedExtraInfoField3: editingProfile.userdefinedExtraInfoField3,
        distributionActive: editingProfile.distributionActive,
        stand005PrinterName: editingProfile.stand005PrinterName,
        profileGroupID: editingProfile.profileGroupID,
        labelPrinterName: editingProfile.labelPrinterName,
        grRejectMissingPO: editingProfile.grRejectMissingPO,
        grPriceAndCurrencyVisible: editingProfile.grPriceAndCurrencyVisible,
        grDefCustomIDNewOrder: editingProfile.grDefCustomIDNewOrder,
        grDefCustomIDFinishOrder: editingProfile.grDefCustomIDFinishOrder,
        scDefCustomIDNewOrder: editingProfile.scDefCustomIDNewOrder,
        scDefCustomIDFinishOrder: editingProfile.scDefCustomIDFinishOrder,
        grAutoLockPO: editingProfile.grAutoLockPO,
        barCodeConfirmLocation: editingProfile.barCodeConfirmLocation,
        barCodeMaxLength: editingProfile.barCodeMaxLength,
        grDefaultPriceFromItem: editingProfile.grDefaultPriceFromItem,
        ordersTxtToERPYesNo: editingProfile.ordersTxtToERPYesNo,
        ordersTxtExportItemNo: editingProfile.ordersTxtExportItemNo,
        defaultPListFilterGroup: editingProfile.defaultPListFilterGroup,
        pListFilterGroupYesNo: editingProfile.pListFilterGroupYesNo,
        pListOverridePriority: editingProfile.pListOverridePriority,
        packageSlipNumOfCopies: editingProfile.packageSlipNumOfCopies,
        packingActive: editingProfile.packingActive,
        pListFinishToPacking: editingProfile.pListFinishToPacking,
        defaultPackZoneFilter: editingProfile.defaultPackZoneFilter,
        pListOrderPackageReportID: editingProfile.pListOrderPackageReportID,
        hidePListSignatureToolButtons: editingProfile.hidePListSignatureToolButtons,
        cloudStorageYesNo: editingProfile.cloudStorageYesNo,
        cloudStorageItemLabelFolder: editingProfile.cloudStorageItemLabelFolder,
        plstand005Reports: editingProfile.plstand005Reports,
        qtyOriginOnInventoryListYesNo: editingProfile.qtyOriginOnInventoryListYesNo,
        cloudStorageStcSortingBoxFolder: editingProfile.cloudStorageStcSortingBoxFolder,
        stcSortingBoxPrinterName: editingProfile.stcSortingBoxPrinterName,
        grCrossDockingYesNo: editingProfile.grCrossDockingYesNo,
        grCrossDockingNumOfDays: editingProfile.grCrossDockingNumOfDays,
        intRelocationActive: editingProfile.intRelocationActive,
        grToIntRelocation: editingProfile.grToIntRelocation,
        allowAdvItemConfigYesNo: editingProfile.allowAdvItemConfigYesNo,
        goodsReceptionVersion: editingProfile.goodsReceptionVersion,
        createNewCustomerOrderYesNo: editingProfile.createNewCustomerOrderYesNo,
        pOrdersFilter: editingProfile.pOrdersFilter,
        pOrderLineFilter: editingProfile.pOrderLineFilter,
        defaultGRBinCode: editingProfile.defaultGRBinCode,
        defaultGRCDBinCode: editingProfile.defaultGRCDBinCode,
        useGRStcSortingBox: editingProfile.useGRStcSortingBox,
        pListAutoCalcMode: editingProfile.pListAutoCalcMode,
        autoPrintPListOrderPackage: editingProfile.autoPrintPListOrderPackage,
        pListWeightRequired: editingProfile.pListWeightRequired,
        pListVolumeRequired: editingProfile.pListVolumeRequired,
        warehouseFilter: editingProfile.warehouseFilter,
        packageReportID: editingProfile.packageReportID,
        grNewLineToLockedPO: editingProfile.grNewLineToLockedPO,
        addOrderLineNoInput: editingProfile.addOrderLineNoInput,
        plUsePListSSCC: editingProfile.plUsePListSSCC,
        cloudStorageSSCCFolder: editingProfile.cloudStorageSSCCFolder,
        extraQtyOnGRConfirm: editingProfile.extraQtyOnGRConfirm,
        orderLineBatchIDOnPList: editingProfile.orderLineBatchIDOnPList,
        useLocationInItemSearchST: editingProfile.useLocationInItemSearchST,
        pListNumOfPalletsRequired: editingProfile.pListNumOfPalletsRequired,
        pickListNavigationFromListView: editingProfile.pickListNavigationFromListView,
        grCheckPutAwaySplit: editingProfile.grCheckPutAwaySplit,
        grDisableEditGRItemBatchNo: editingProfile.grDisableEditGRItemBatchNo,
        profileLogLevel: editingProfile.profileLogLevel,
        shipmentProposalCustomField: editingProfile.shipmentProposalCustomField,
        stand005PrinterNo: editingProfile.stand005PrinterNo,
        stcSortingBoxPrinterNo: editingProfile.stcSortingBoxPrinterNo,
        labelPrinterNo: editingProfile.labelPrinterNo,
        allowedToChangeLocation: editingProfile.allowedToChangeLocation,
        pListNumOfPackagesRequired: editingProfile.pListNumOfPackagesRequired,
        locationNameRequired: editingProfile.locationNameRequired,
        extraQtyOnGRNotAllowed: editingProfile.extraQtyOnGRNotAllowed,
        plstand005ReportsNumOfCopies: editingProfile.plstand005ReportsNumOfCopies,
      };

      const updated = await userProfileService.updateCurrentUserProfile(updateRequest);
      setProfile(updated);
      setEditingProfile(updated);
      setIsEditing(false);
      
      // If language was changed, update i18n system
      if (editingProfile.langID && editingProfile.langID !== profile?.langID) {
        const newLang = editingProfile.langID.toLowerCase() as SupportedLanguage;
        if (newLang === 'nb' || newLang === 'sv' || newLang === 'en') {
          setLanguage(newLang);
        }
      }
    } catch (err) {
      setError('Kunne ikke lagre profil. Vennligst prøv igjen.');
      console.error('Failed to save profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (fieldName: keyof UserProfileDto, value: any) => {
    if (editingProfile) {
      setEditingProfile({
        ...editingProfile,
        [fieldName]: value
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-600 text-center">{error}</div>
        <Button onClick={loadProfile} variant="primary">
          Prøv igjen
        </Button>
      </div>
    );
  }

  if (!profile || !editingProfile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Ingen profil funnet</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Brukerprofil</h2>
          <p className="text-gray-600">Vis og rediger dine profilinnstillinger</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button
                onClick={handleCancel}
                variant="secondary"
                disabled={saving}
              >
                <X size={16} />
                Avbryt
              </Button>
              <Button
                onClick={handleSave}
                variant="success"
                disabled={saving}
              >
                <Save size={16} />
                {saving ? 'Lagrer...' : 'Lagre'}
              </Button>
            </>
          ) : (
            <Button onClick={handleEdit} variant="primary">
              <Edit size={16} />
              Rediger
            </Button>
          )}
        </div>
      </div>

      {/* General Information */}
      <ProfileSection 
        title="Generell informasjon" 
        icon={<User size={20} className="text-blue-600" />}
        isEditing={isEditing}
      >
        <ProfileField
          label="Selskap"
          value={editingProfile.companyNo}
          readOnly
        />
        <ProfileField
          label="Kunde"
          value={editingProfile.customerNo}
          readOnly
        />
        <ProfileField
          label="Bruker-ID"
          value={editingProfile.userID}
          readOnly
        />
        <ProfileField
          label="Profil-ID"
          value={editingProfile.profileID}
          isEditing={isEditing}
          onChange={(value) => updateField('profileID', value)}
        />
        <ProfileField
          label="Profilnavn"
          value={editingProfile.profileName}
          isEditing={isEditing}
          onChange={(value) => updateField('profileName', value)}
        />
        <ProfileField
          label="Operatør"
          value={editingProfile.operator}
          isEditing={isEditing}
          onChange={(value) => updateField('operator', value)}
        />
        <ProfileField
          label="Språk"
          value={editingProfile.langID || 'nb'}
          type="select"
          isEditing={isEditing}
          onChange={(value) => updateField('langID', value)}
          options={[
            { value: 'nb', label: 'Norsk bokmål' },
            { value: 'sv', label: 'Svenska' },
            { value: 'en', label: 'English' }
          ]}
        />
        <ProfileField
          label="Standard lager"
          value={editingProfile.defaultWarehouse}
          isEditing={isEditing}
          onChange={(value) => updateField('defaultWarehouse', value)}
        />
      </ProfileSection>

      {/* Connection Settings */}
      <ProfileSection 
        title="Tilkoblingsinnstillinger" 
        icon={<Wifi size={20} className="text-green-600" />}
        isEditing={isEditing}
      >
        <ProfileField
          label="HTTP Hostname"
          value={editingProfile.httpHostname}
          isEditing={isEditing}
          onChange={(value) => updateField('httpHostname', value)}
        />
        <ProfileField
          label="HTTP Port"
          value={editingProfile.httpPort}
          type="number"
          isEditing={isEditing}
          onChange={(value) => updateField('httpPort', value)}
        />
        <ProfileField
          label="Protokoll"
          value={editingProfile.protocol}
          isEditing={isEditing}
          onChange={(value) => updateField('protocol', value)}
        />
        <ProfileField
          label="TCP Hostname"
          value={editingProfile.tcpHostname}
          isEditing={isEditing}
          onChange={(value) => updateField('tcpHostname', value)}
        />
        <ProfileField
          label="TCP Port"
          value={editingProfile.tcpPort}
          type="number"
          isEditing={isEditing}
          onChange={(value) => updateField('tcpPort', value)}
        />
        <ProfileField
          label="Koble til ved oppstart"
          value={editingProfile.connectOnStartup}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('connectOnStartup', value)}
        />
      </ProfileSection>

      {/* Module Activation */}
      <ProfileSection 
        title="Modulaktivering" 
        icon={<Settings size={20} className="text-purple-600" />}
        isEditing={isEditing}
      >
        <ProfileField
          label="Plukklistemodul aktiv"
          value={editingProfile.pickListActive}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('pickListActive', value)}
        />
        <ProfileField
          label="Varemottak aktiv"
          value={editingProfile.goodsReceptionActive}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('goodsReceptionActive', value)}
        />
        <ProfileField
          label="Lagertelling aktiv"
          value={editingProfile.stockCountingActive}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('stockCountingActive', value)}
        />
        <ProfileField
          label="Pakking aktiv"
          value={editingProfile.packingActive}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('packingActive', value)}
        />
        <ProfileField
          label="Distribusjon aktiv"
          value={editingProfile.distributionActive}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('distributionActive', value)}
        />
        <ProfileField
          label="Intern flytting aktiv"
          value={editingProfile.intRelocationActive}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('intRelocationActive', value)}
        />
      </ProfileSection>

      {/* Printer Settings */}
      <ProfileSection
        title="Skriverinnstillinger"
        icon={<Printer size={20} className="text-orange-600" />}
        isEditing={isEditing}
      >
        {printersError && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm flex items-center justify-between">
            <span>{printersError}</span>
            <button
              onClick={loadPrinters}
              className="text-yellow-600 hover:text-yellow-800 underline ml-2"
            >
              Prøv igjen
            </button>
          </div>
        )}
        <ProfileField
          label="Skriver-ID (legacy)"
          value={editingProfile.printerID}
          isEditing={isEditing}
          onChange={(value) => updateField('printerID', value)}
        />
        <PrinterSelectField
          label="Etikett-skriver (PrintNode)"
          value={editingProfile.labelPrinterNo}
          printers={printers}
          isEditing={isEditing}
          loading={printersLoading}
          onChange={(value) => updateField('labelPrinterNo', value)}
        />
        <PrinterSelectField
          label="SSCC/Pall-skriver (PrintNode)"
          value={editingProfile.stand005PrinterNo}
          printers={printers}
          isEditing={isEditing}
          loading={printersLoading}
          onChange={(value) => updateField('stand005PrinterNo', value)}
        />
        <PrinterSelectField
          label="Sorteringsboks-skriver (PrintNode)"
          value={editingProfile.stcSortingBoxPrinterNo}
          printers={printers}
          isEditing={isEditing}
          loading={printersLoading}
          onChange={(value) => updateField('stcSortingBoxPrinterNo', value)}
        />
        <ProfileField
          label="Pakkseddel-skriver"
          value={editingProfile.packageSlipPrinterName}
          isEditing={isEditing}
          onChange={(value) => updateField('packageSlipPrinterName', value)}
        />
        <ProfileField
          label="Fraktlapp-skriver"
          value={editingProfile.shippingLabelsPrinterName}
          isEditing={isEditing}
          onChange={(value) => updateField('shippingLabelsPrinterName', value)}
        />
        <ProfileField
          label="Følgeseddel-skriver"
          value={editingProfile.consignmentNotePrinterName}
          isEditing={isEditing}
          onChange={(value) => updateField('consignmentNotePrinterName', value)}
        />
        <ProfileField
          label="Pakke-skriver"
          value={editingProfile.packagePrinterID}
          isEditing={isEditing}
          onChange={(value) => updateField('packagePrinterID', value)}
        />
      </ProfileSection>

      {/* Barcode Settings */}
      <ProfileSection 
        title="Strekkode-innstillinger" 
        icon={<Package size={20} className="text-red-600" />}
        isEditing={isEditing}
      >
        <ProfileField
          label="Bekreft vare med strekkode"
          value={editingProfile.barCodeConfirmItem}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('barCodeConfirmItem', value)}
        />
        <ProfileField
          label="Bekreft pakke med strekkode"
          value={editingProfile.barCodeConfirmPackage}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('barCodeConfirmPackage', value)}
        />
        <ProfileField
          label="Bekreft lokasjon med strekkode"
          value={editingProfile.barCodeConfirmLocation}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('barCodeConfirmLocation', value)}
        />
        <ProfileField
          label="Maksimal strekkode-lengde"
          value={editingProfile.barCodeMaxLength}
          type="number"
          isEditing={isEditing}
          onChange={(value) => updateField('barCodeMaxLength', value)}
        />
      </ProfileSection>

      {/* Cloud Storage Settings */}
      <ProfileSection 
        title="Skylagring-innstillinger" 
        icon={<Database size={20} className="text-indigo-600" />}
        isEditing={isEditing}
      >
        <ProfileField
          label="Skylagring aktiv"
          value={editingProfile.cloudStorageYesNo}
          type="boolean"
          isEditing={isEditing}
          onChange={(value) => updateField('cloudStorageYesNo', value)}
        />
        <ProfileField
          label="Vareetikett-mappe"
          value={editingProfile.cloudStorageItemLabelFolder}
          isEditing={isEditing}
          onChange={(value) => updateField('cloudStorageItemLabelFolder', value)}
        />
        <ProfileField
          label="SSCC-mappe"
          value={editingProfile.cloudStorageSSCCFolder}
          isEditing={isEditing}
          onChange={(value) => updateField('cloudStorageSSCCFolder', value)}
        />
        <ProfileField
          label="Sorteringsboks-mappe"
          value={editingProfile.cloudStorageStcSortingBoxFolder}
          isEditing={isEditing}
          onChange={(value) => updateField('cloudStorageStcSortingBoxFolder', value)}
        />
      </ProfileSection>

      {/* System Information */}
      <ProfileSection 
        title="Systeminformasjon" 
        icon={<Shield size={20} className="text-gray-600" />}
      >
        <ProfileField
          label="ID"
          value={editingProfile.id}
          readOnly
        />
        <ProfileField
          label="Profilgruppe-ID"
          value={editingProfile.profileGroupID}
          readOnly
        />
        <ProfileField
          label="Sist oppdatert"
          value={editingProfile.lastUpdate}
          type="date"
          readOnly
        />
        <ProfileField
          label="Oppdatert av"
          value={editingProfile.updatedBy}
          readOnly
        />
        <ProfileField
          label="Opprettet"
          value={editingProfile.created}
          type="date"
          readOnly
        />
        <ProfileField
          label="Opprettet av"
          value={editingProfile.createdBy}
          readOnly
        />
      </ProfileSection>
    </div>
  );
};
